import Zdog from "zdog";

function setupLoadingScreen() {
    const loadingScreen = document.querySelector("[data-loading-screen]");
    if (!loadingScreen) return;

    const body = document.body;
    const messageEl = loadingScreen.querySelector("[data-loading-screen-message]");
    const logoCanvas = loadingScreen.querySelector("[data-tredo-logo]");
    let debugConfig = window.TREDO_LOADING_DEBUG || {};
    let debugApi = null;
    const debugEnabled = debugConfig.enabled === true;
    const keepVisible = debugConfig.keepVisible === true || debugEnabled === true;
    let disableAutoHide = debugConfig.disableAutoHide === true || keepVisible === true;

    let rafId = null;
    let stopZdogAnimation = null;

    const initZdogLogo = (attempt = 0) => {
        if (!logoCanvas) return;
        if (!Zdog) {
            if (attempt < 50) {
                window.setTimeout(() => initZdogLogo(attempt + 1), 100);
            }
            return;
        }

        let isSpinning = false;
        const illo = new Zdog.Illustration({
            element: logoCanvas,
            dragRotate: true,
            color: "#fff",
            onDragStart: function () {
                isSpinning = false;
            },
            onDragEnd: function () {
                isSpinning = true;
            },
        });

        illo.rotate.y = 0.8;
        window.setTimeout(() => {
            isSpinning = true;
        }, 1000);

        const TAU = Zdog.TAU;
        let rectStroke = 4;
        let rectColor = "#000";
        let width = 60;
        let height = 50;
        let depth = 20;

        const canvasSize = Math.min(logoCanvas.width || 0, logoCanvas.height || 0);
        if (canvasSize >= 110) {
            rectStroke = 4;
            width = 80;
            height = 50;
            depth = 20;
        } else if (canvasSize >= 70) {
            rectStroke = 3;
            width = 60;
            height = 36;
            depth = 15;
        } else {
            rectStroke = 2;
            width = 40;
            height = 25;
            depth = 10;
        }

        const boxTop = new Zdog.Box({
            addTo: illo,
            width: width,
            height: depth,
            depth: depth,
            stroke: false,
            color: "#000",
            leftFace: "#000",
            rightFace: "#000",
            rearFace: "#fff",
            frontFace: "#fff",
            topFace: "#000",
            bottomFace: "#000",
            translate: { y: -(height + depth) / 2 },
        });

        const rect01 = new Zdog.Rect({
            addTo: illo,
            width: width,
            height: depth,
            stroke: rectStroke,
            color: rectColor,
            translate: { y: -(height + depth) / 2, z: depth / 2 + 0.1 },
        });
        const rect02 = new Zdog.Rect({
            addTo: illo,
            width: width,
            height: depth,
            stroke: rectStroke,
            color: rectColor,
            translate: { y: -(height + depth) / 2, z: -(depth / 2 + 0.1) },
        });
        const rect03 = new Zdog.Rect({
            addTo: illo,
            width: depth,
            height: depth,
            stroke: rectStroke,
            color: rectColor,
            translate: { y: -(height + depth) / 2, x: width / 2 + 0.1 },
            rotate: { y: TAU / 4 },
        });
        const rect04 = new Zdog.Rect({
            addTo: illo,
            width: depth,
            height: depth,
            stroke: rectStroke,
            color: rectColor,
            translate: { y: -(height + depth) / 2, x: -(width / 2 + 0.1) },
            rotate: { y: TAU / 4 },
        });

        const boxBottom = new Zdog.Box({
            addTo: illo,
            width: depth,
            height: height,
            depth: depth,
            stroke: false,
            color: "#000",
            leftFace: "#fff",
            rightFace: "#fff",
            rearFace: "#000",
            frontFace: "#000",
            topFace: "#000",
            bottomFace: "#000",
            translate: { y: 1 },
        });

        const rect05 = new Zdog.Rect({
            addTo: illo,
            width: depth,
            height: height,
            stroke: rectStroke,
            color: rectColor,
            translate: { y: 1, z: depth / 2 + 0.1 },
        });
        const rect06 = new Zdog.Rect({
            addTo: illo,
            width: depth,
            height: height,
            stroke: rectStroke,
            color: rectColor,
            translate: { y: 1, z: -(depth / 2 + 0.1) },
        });
        const rect07 = new Zdog.Rect({
            addTo: illo,
            width: depth,
            height: height,
            stroke: rectStroke,
            color: rectColor,
            translate: { y: 1, x: depth / 2 + 0.1 },
            rotate: { y: TAU / 4 },
        });
        const rect08 = new Zdog.Rect({
            addTo: illo,
            width: depth,
            height: height,
            stroke: rectStroke,
            color: rectColor,
            translate: { y: 1, x: -(depth / 2 + 0.1) },
            rotate: { y: TAU / 4 },
        });

        let boxTopTranslate = -(height + depth) / 2;
        let goingUp = true;
        let animationStep = 0.5;
        let wait = 40;

        let lastFrameTime = 0;
        const frameInterval = 1000 / 30;
        const animate = (time = 0) => {
            if (time - lastFrameTime >= frameInterval) {
                lastFrameTime = time;
                illo.rotate.y += isSpinning ? 0.01 : 0;
                illo.updateRenderGraph();
            }
            rafId = requestAnimationFrame(animate);
        };
        animate();

        stopZdogAnimation = () => {
            if (rafId) {
                cancelAnimationFrame(rafId);
                rafId = null;
            }
        };
    };

    const show = () => {
        loadingScreen.classList.remove("is-hidden");
        loadingScreen.classList.remove("is-hiding");
        body.classList.add("loading-screen-active");
    };

    const hide = () => {
        loadingScreen.classList.add("is-hiding");
        body.classList.remove("loading-screen-active");

        window.setTimeout(() => {
            loadingScreen.classList.add("is-hidden");
            if (typeof stopZdogAnimation === "function") {
                stopZdogAnimation();
            }
        }, 650);
    };

    const setMessage = (text) => {
        if (messageEl && typeof text === "string") {
            messageEl.textContent = text;
        }
    };

    const setHtml = (html) => {
        if (messageEl && typeof html === "string") {
            messageEl.innerHTML = html;
        }
    };

    debugApi = {
        element: loadingScreen,
        show,
        hide,
        setMessage,
        setHtml,
    };

    const applyDebugConfig = (nextConfig = {}) => {
        debugConfig = { ...debugConfig, ...nextConfig };
        const nextDebugEnabled = debugConfig.enabled === true;
        const nextKeepVisible = debugConfig.keepVisible === true || nextDebugEnabled === true;
        disableAutoHide = debugConfig.disableAutoHide === true || nextKeepVisible === true;

        if (nextKeepVisible) {
            show();
            loadingScreen.classList.add("is-debug");
        } else {
            loadingScreen.classList.remove("is-debug");
        }

        Object.assign(debugApi, debugConfig);
    };

    show();
    initZdogLogo();

    const hideOnLoad = () => {
        if (!disableAutoHide) {
            hide();
        }
    };

    if (document.readyState === "complete") {
        hideOnLoad();
    } else {
        window.addEventListener("load", hideOnLoad, { once: true });
    }

    Object.defineProperty(window, "TREDO_LOADING_DEBUG", {
        configurable: true,
        get() {
            return debugApi;
        },
        set(value) {
            applyDebugConfig(value || {});
        },
    });

    applyDebugConfig(debugConfig);
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", setupLoadingScreen);
} else {
    setupLoadingScreen();
}
